import React, { useState, useMemo, useCallback, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";

import "./aggrid.css"; // Import custom styles

import DefaultLoader from "./DefaultLoader"; // Add this import
import TableControls from "./TableControls";

import { SERVER_SORTABLE_FIELDS } from "../utils/constants";
import CustomHeaderComponent from "./CustomHeaderComponent";
import { useLocalStorage } from "../utils/localstorage";

// Register necessary modules
ModuleRegistry.registerModules([AllCommunityModule]);

const AgGridTable = React.memo(
  ({
    tableId = null,
    columnDefs,
    rowData,
    defaultColDef,
    defaultVisibleColumns = [],
    showThemeSelector = false,

    gridOptions,
    containerStyle,
    headerContent,
    customPaginationComponent,
    handleRefreshClick,

    // Server-side pagination and advanced features
    loading = false,
    error = null,
    enableRowSelection = false,
    onRowSelectionChanged = () => {},
    selectedRows = [],
    bulkActionComponent = null,
    currentSort = "",
    onSortChanged = () => {},
    onFilterChanged = () => {},
    currentFilters = {},
    resetServerFilters = false,
    handleResetFilters = () => {},
    onClearSelectedRows = () => {},

    ...restProps
  }) => {
    const [gridApi, setGridApi] = useState();

    const [isResettingFilters, setIsResettingFilters] = useState();

    const columnStorageKey = useMemo(() => {
      return tableId ? `ag-grid-${tableId}-columns` : null;
    }, [tableId]);

    const [selectedColumns, setSelectedColumns] = useLocalStorage(
      columnStorageKey,
      defaultVisibleColumns
    );

    const handleGridReady = useCallback((params) => {
      setGridApi(params.api);
    }, []);

    // Enhanced resetFilters function with loading state
    const handleServerResetFilters = useCallback(() => {
      if (gridApi) {
        gridApi.setFilterModel(null);
        gridApi.resetColumnState();
      }
      setIsResettingFilters(true);
      handleResetFilters();
    }, [handleResetFilters, gridApi]);

    useEffect(() => {
      if (!isResettingFilters) return;
      const timeoutId = setTimeout(() => {
        setIsResettingFilters(false);
      }, 500);
      return () => clearTimeout(timeoutId);
    }, [isResettingFilters]);

    // Function to handle column selection change

    const filteredColumns = useMemo(
      () => columnDefs.filter((col) => selectedColumns.includes(col.field)),
      [columnDefs, selectedColumns]
    );

    // Enhanced column definitions for server-side mode

    const enhancedColumnDefs = useMemo(() => {
      // For server-side, add custom header components
      return filteredColumns.map((col) => {
        const isSortable = SERVER_SORTABLE_FIELDS.includes(col.field);

        let headerClass = "";
        if (isSortable) {
          const [sortField, sortDirection] = currentSort
            ? currentSort.split(":")
            : [null, null];
          const isCurrentSortField =
            col.field === sortField ||
            (col.field === "workflowId" &&
              sortField === "workflowId.keyword") ||
            (col.field === "workflowType" &&
              sortField === "workflowType.keyword");

          if (isCurrentSortField) {
            headerClass = "sorted-column-header";
          }
        }

        return {
          ...col,
          headerComponentParams: {
            displayName: col.headerName,
            currentSort: currentSort,
            onSortChanged: onSortChanged,
            onFilterChanged: onFilterChanged,
            currentFilters: currentFilters,
            isWorkflowContext: restProps.isWorkflowContext,
          },
          headerComponent: CustomHeaderComponent,
          headerClass: headerClass,
          sortable: false,
        };
      });
    }, [
      filteredColumns,
      currentSort,
      onSortChanged,
      currentFilters,
      onFilterChanged,
      restProps.isWorkflowContext,
    ]);

    // Row selection handling
    const handleSelectionChanged = useCallback(() => {
      if (gridApi && enableRowSelection) {
        const selectedNodes = gridApi.getSelectedNodes();
        const selectedData = selectedNodes.map((node) => node.data);
        onRowSelectionChanged(selectedData);
      }
    }, [gridApi, onRowSelectionChanged, enableRowSelection]);

    // Update grid selection when selectedRows changes externally
    useEffect(() => {
      if (gridApi && enableRowSelection) {
        if (selectedRows && selectedRows.length > 0) {
          const selectedIds = selectedRows.map((row) => row.workflowId);

          let selectionChanged = false;
          gridApi.forEachNode((node) => {
            const shouldBeSelected =
              node.data && selectedIds.includes(node.data.workflowId);
            const isSelected = node.isSelected();

            if (shouldBeSelected !== isSelected) {
              selectionChanged = true;
              node.setSelected(shouldBeSelected);
            }
          });
        } else if (gridApi.getSelectedNodes().length > 0) {
          gridApi.deselectAll();
        }
      }
    }, [gridApi, selectedRows, enableRowSelection, rowData]);

    const rowSelectionConfig = useMemo(() => {
      return enableRowSelection === "True" ? { mode: "multiRow" } : undefined;
    }, [enableRowSelection]);

    // Normalize to boolean: support both boolean true and string "True"
    const isRowSelectionEnabled = useMemo(
      () => enableRowSelection === true || enableRowSelection === "True",
      [enableRowSelection]
    );

    // Handle loading/error states
    useEffect(() => {
      if (gridApi) {
        if (error) {
          gridApi.showNoRowsOverlay();
        } else if (loading) {
          gridApi.showLoadingOverlay();
        } else if (!loading && rowData?.length > 0) {
          gridApi.hideOverlay();
        } else if (!loading && (!rowData || rowData.length === 0)) {
          gridApi.showNoRowsOverlay();
        }
      }
    }, [loading, error, gridApi, rowData]);

    return (
      <div className="server-side-datatable">
        {/* Add loading overlay */}
        <div className="loading-overlay">
          <DefaultLoader
            displayModal={isResettingFilters}
            message="Resetting filters..."
            contentStyle={{ width: "300px" }}
          />
        </div>

        <div className="table-header">
          <TableControls
            resetFiltersFunction={handleServerResetFilters}
            currentFilters={currentFilters}
            handleRefreshClick={handleRefreshClick}
            columnDefs={columnDefs}
            selectedColumns={selectedColumns}
            defaultVisibleColumns={defaultVisibleColumns}
            onColumnSelectionChange={setSelectedColumns}
            GlobalSearch={restProps?.GlobalSearch}
            enableRowSelection={enableRowSelection}
            selectedRows={selectedRows}
            bulkActionComponent={bulkActionComponent}
            actionButton={restProps?.actionButton}
            onClearSelectedRows={onClearSelectedRows}
          />
        </div>

        <div className="custom-ag-table-container" style={containerStyle}>
          <AgGridReact
            style={{ width: "100%", height: "100%" }}
            rowData={rowData}
            columnDefs={enhancedColumnDefs}
            defaultColDef={defaultColDef}
            headerHeight={36}
            rowHeight={36}
            onGridReady={handleGridReady}
            gridOptions={gridOptions}
            // Provide a stable row ID so selection survives data refresh/pagination
            getRowId={
              isRowSelectionEnabled
                ? (params) => params?.data?.workflowId
                : undefined
            }
            rowSelection={rowSelectionConfig}
            suppressColumnVirtualisation={true}
            onSelectionChanged={
              enableRowSelection ? handleSelectionChanged : undefined
            }
            suppressDragLeaveHidesColumns={true}
            {...restProps}
          />
        </div>
      </div>
    );
  }
);

export default AgGridTable;
